import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { fetchMuseumActivityStoryGroups } from '../utils/museumActivityInsights';
import {
  persistSalonStoryGroups,
  readCachedSalonStoryGroups,
  shouldRevalidateSalonStories,
  warmSalonStoryCovers,
} from '../utils/artistSalonCache';
import ArtMuseumInsightsStories from './ArtMuseumInsightsStories';

/** Artist Salon story rings — Instagram-style: cached tray first, background refresh. */
export default function ArtistWallMuseumInsights({ uid, placement = 'inline' }) {
  const isProfileRow = placement === 'profile';
  const [loadingStories, setLoadingStories] = useState(() => {
    const cached = readCachedSalonStoryGroups(uid);
    return !(cached && cached.length > 0);
  });
  const [revalidating, setRevalidating] = useState(false);
  const [stories, setStories] = useState(() => readCachedSalonStoryGroups(uid) || []);
  const revalidateLock = useRef(false);

  useEffect(() => {
    if (!uid) {
      setLoadingStories(false);
      setStories([]);
      setRevalidating(false);
      return;
    }

    let cancelled = false;

    const cached = readCachedSalonStoryGroups(uid);
    if (cached?.length) {
      setStories(cached);
      setLoadingStories(false);
      warmSalonStoryCovers(cached);
    }

    const needsFetch = shouldRevalidateSalonStories(uid) || !cached?.length;
    if (!needsFetch) {
      return () => {
        cancelled = true;
      };
    }

    if (revalidateLock.current) {
      return () => {
        cancelled = true;
      };
    }
    revalidateLock.current = true;

    const revalidate = async () => {
      if (cached?.length) {
        setRevalidating(true);
      } else {
        setLoadingStories(true);
      }

      try {
        const fresh = await fetchMuseumActivityStoryGroups(uid);
        if (cancelled) return;
        persistSalonStoryGroups(uid, fresh);
        setStories(fresh);
        warmSalonStoryCovers(fresh);
      } catch (err) {
        console.warn('[ArtistWallMuseumInsights] Failed to load activity stories:', err);
        if (!cancelled && !cached?.length) setStories([]);
      } finally {
        if (!cancelled) {
          setLoadingStories(false);
          setRevalidating(false);
        }
        revalidateLock.current = false;
      }
    };

    void revalidate();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const showStories = loadingStories || stories.length > 0;

  if (!showStories) {
    return null;
  }

  const hasStoriesContent = loadingStories || stories.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={
        isProfileRow
          ? 'w-full min-w-0 border-0 bg-transparent p-0 shadow-none md:rounded-2xl md:border md:border-[#e8d4bc]/80 md:bg-gradient-to-br md:from-[#fffdf9]/95 md:to-[#fff6ec]/70 md:p-2.5 md:shadow-sm'
          : 'mb-3 rounded-2xl border border-[#e8d4bc]/80 bg-gradient-to-br from-[#fffdf9]/95 to-[#fff6ec]/70 p-2.5 shadow-sm md:p-3'
      }
    >
      {hasStoriesContent && (
        <div className={`min-w-0 overflow-hidden ${revalidating ? 'opacity-95' : ''}`}>
          <ArtMuseumInsightsStories
            stories={stories}
            loading={loadingStories}
            embedded
            profileStrip={isProfileRow}
          />
        </div>
      )}
    </motion.div>
  );
}
